/**
 * iter-119bh Phase 8+9 — Analytical + niche chart renderers that recharts
 * does NOT ship natively. Each accepts the standard widget `w` shape:
 *   { id, title, config: { data, x, y, value, target, min, max, ... } }
 *
 * Phase 7 wow charts (scatter, treemap, heatmap) live inline in
 * Widgets.jsx because they're trivial. Anything that needs custom SVG
 * (liquid fill, gauge, calendar heatmap, boxplot, network graph,
 * candlestick) lives here so Widgets.jsx stays readable.
 *
 * NO emojis. Lucide icons + CSS animations only. Self-contained — no
 * data-fetching, no API calls. The caller passes `data` already shaped.
 */
import React from "react";
import { Badge } from "@/components/ui/badge";
import { CHART_COLORS, fmtNum, fmtAxis } from "./chartFormat";

function WidgetFrame({ w, stale, children, testIdSuffix }) {
    const cfg = w.config || {};
    return (
        <div className="border border-border bg-card rounded-sm p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
            <div className="flex items-start justify-between mb-1">
                <div className="eyebrow">{w.title}</div>
                {stale && (
                    <Badge variant="outline" className="text-[9px] font-mono border-amber-500/40 text-amber-600">stale</Badge>
                )}
            </div>
            <div className={`flex-1 mt-3 min-h-0 relative${testIdSuffix ? "" : ""}`}
                data-testid={testIdSuffix ? `widget-${w.id}-${testIdSuffix}` : undefined}>
                {children(cfg)}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────
// LIQUID FILL — animated wave gauge showing percent of target
// ─────────────────────────────────────────────────────────────────────
export function LiquidFillWidget({ w, stale }) {
    return (
        <WidgetFrame w={w} stale={stale} testIdSuffix="liquid">
            {(cfg) => {
                const pct = Math.max(0, Math.min(1, Number(cfg.value || 0)));
                const fillY = 100 - pct * 100;
                return (
                    <div className="flex items-center justify-center h-full">
                        <svg viewBox="0 0 100 100" className="w-full max-w-[180px] h-full">
                            <defs>
                                <clipPath id={`lf-clip-${w.id}`}>
                                    <circle cx="50" cy="50" r="42" />
                                </clipPath>
                            </defs>
                            <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--border))" strokeWidth="2" />
                            <g clipPath={`url(#lf-clip-${w.id})`}>
                                <rect x="0" y={fillY} width="100" height="100" fill={cfg.color || CHART_COLORS[0]} opacity="0.85">
                                    <animate attributeName="x" from="-10" to="0" dur="2.4s" repeatCount="indefinite" />
                                </rect>
                                <path d={`M0,${fillY} Q25,${fillY - 4} 50,${fillY} T100,${fillY} V100 H0 Z`}
                                    fill={cfg.color || CHART_COLORS[0]} opacity="0.45">
                                    <animate attributeName="d"
                                        values={`M0,${fillY} Q25,${fillY - 4} 50,${fillY} T100,${fillY} V100 H0 Z;
                                                 M0,${fillY} Q25,${fillY + 4} 50,${fillY} T100,${fillY} V100 H0 Z;
                                                 M0,${fillY} Q25,${fillY - 4} 50,${fillY} T100,${fillY} V100 H0 Z`}
                                        dur="3s" repeatCount="indefinite" />
                                </path>
                            </g>
                            <text x="50" y="55" textAnchor="middle" fontSize="18" fontWeight="600"
                                fill={pct > 0.5 ? "white" : "hsl(var(--foreground))"}>{Math.round(pct * 100)}%</text>
                        </svg>
                    </div>
                );
            }}
        </WidgetFrame>
    );
}

// ─────────────────────────────────────────────────────────────────────
// GAUGE — semicircle arc + needle to current value / target
// ─────────────────────────────────────────────────────────────────────
export function GaugeWidget({ w, stale }) {
    return (
        <WidgetFrame w={w} stale={stale} testIdSuffix="gauge">
            {(cfg) => {
                const min = Number(cfg.min ?? 0);
                const max = Number(cfg.max ?? 100);
                const value = Math.max(min, Math.min(max, Number(cfg.value || 0)));
                const pct = (value - min) / Math.max(max - min, 1);
                const angle = -90 + pct * 180; // -90deg..+90deg
                // Build path for needle
                const rad = (angle * Math.PI) / 180;
                const nx = 50 + 38 * Math.cos(rad);
                const ny = 60 + 38 * Math.sin(rad);
                return (
                    <div className="flex items-center justify-center h-full">
                        <svg viewBox="0 0 100 70" className="w-full max-w-[220px] h-full">
                            {/* Track */}
                            <path d="M10,60 A40,40 0 0 1 90,60" fill="none" stroke="hsl(var(--border))" strokeWidth="6" />
                            {/* Active arc */}
                            <path d="M10,60 A40,40 0 0 1 90,60" fill="none"
                                stroke={cfg.color || CHART_COLORS[0]} strokeWidth="6"
                                strokeDasharray={`${pct * 126} 126`} strokeLinecap="round" />
                            {/* Needle */}
                            <line x1="50" y1="60" x2={nx} y2={ny} stroke="hsl(var(--foreground))" strokeWidth="2" strokeLinecap="round" />
                            <circle cx="50" cy="60" r="3" fill="hsl(var(--foreground))" />
                            <text x="50" y="40" textAnchor="middle" fontSize="13" fontWeight="600" fill="hsl(var(--foreground))">
                                {fmtNum(value)}
                            </text>
                            <text x="10" y="68" fontSize="6" fill="hsl(var(--muted-foreground))">{fmtAxis(min)}</text>
                            <text x="90" y="68" fontSize="6" textAnchor="end" fill="hsl(var(--muted-foreground))">{fmtAxis(max)}</text>
                        </svg>
                    </div>
                );
            }}
        </WidgetFrame>
    );
}

// ─────────────────────────────────────────────────────────────────────
// CALENDAR HEATMAP — year × week grid (GitHub-style activity matrix)
// ─────────────────────────────────────────────────────────────────────
export function CalendarHeatmapWidget({ w, stale }) {
    return (
        <WidgetFrame w={w} stale={stale} testIdSuffix="cal-heatmap">
            {(cfg) => {
                // Expects cfg.data = [{date: "2026-01-15", value: number}, ...]
                const data = Array.isArray(cfg.data) ? cfg.data : [];
                const map = {};
                let maxV = 0;
                data.forEach((d) => {
                    if (!d?.date) return;
                    const v = Number(d.value) || 0;
                    map[d.date] = v;
                    if (v > maxV) maxV = v;
                });
                // Build a 53-week × 7-day grid ending today
                const today = new Date();
                const weeks = 53;
                const cells = [];
                for (let w = 0; w < weeks; w++) {
                    const col = [];
                    for (let d = 0; d < 7; d++) {
                        const offsetDays = (weeks - 1 - w) * 7 + (6 - d);
                        const dt = new Date(today);
                        dt.setDate(today.getDate() - offsetDays);
                        const iso = dt.toISOString().slice(0, 10);
                        col.push({ iso, value: map[iso] || 0 });
                    }
                    cells.push(col);
                }
                return (
                    <div className="h-full w-full overflow-x-auto">
                        <svg viewBox={`0 0 ${weeks * 12 + 30} 100`} className="w-full" preserveAspectRatio="xMinYMid meet">
                            {["Mon", "", "Wed", "", "Fri", "", ""].map((lbl, i) => (
                                <text key={`lbl-${i}`} x="0" y={20 + i * 12} fontSize="6" fill="hsl(var(--muted-foreground))">{lbl}</text>
                            ))}
                            {cells.map((col, wi) =>
                                col.map((cell, di) => {
                                    const intensity = maxV > 0 ? cell.value / maxV : 0;
                                    const bg = cell.value === 0
                                        ? "hsl(var(--border))"
                                        : `hsla(var(--primary-h, 25), 85%, 50%, ${0.15 + intensity * 0.85})`;
                                    return (
                                        <rect key={`${wi}-${di}`} x={20 + wi * 12} y={12 + di * 12}
                                            width="10" height="10" rx="1.5" fill={bg}>
                                            <title>{cell.iso}: {fmtNum(cell.value)}</title>
                                        </rect>
                                    );
                                })
                            )}
                        </svg>
                    </div>
                );
            }}
        </WidgetFrame>
    );
}

// ─────────────────────────────────────────────────────────────────────
// BOXPLOT — min / Q1 / median / Q3 / max per category
// ─────────────────────────────────────────────────────────────────────
export function BoxplotWidget({ w, stale }) {
    return (
        <WidgetFrame w={w} stale={stale} testIdSuffix="boxplot">
            {(cfg) => {
                // Expects cfg.data = [{label, min, q1, median, q3, max}, ...]
                const data = Array.isArray(cfg.data) ? cfg.data : [];
                if (!data.length) {
                    return <div className="flex items-center justify-center h-full text-xs text-muted-foreground">No data</div>;
                }
                const allValues = data.flatMap((d) => [d.min, d.q1, d.median, d.q3, d.max].filter((v) => v != null));
                const dataMin = Math.min(...allValues, 0);
                const dataMax = Math.max(...allValues, 1);
                const range = dataMax - dataMin || 1;
                const yScale = (v) => 90 - ((v - dataMin) / range) * 80;
                const widthPerBox = Math.max(20, 100 / Math.max(data.length, 1));
                return (
                    <svg viewBox={`0 0 ${Math.max(120, data.length * 28)} 100`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
                        <line x1="0" y1="90" x2={Math.max(120, data.length * 28)} y2="90" stroke="hsl(var(--border))" strokeWidth="0.5" />
                        {data.map((d, i) => {
                            const cx = i * 28 + 18;
                            const boxTop = yScale(d.q3);
                            const boxBottom = yScale(d.q1);
                            const medianY = yScale(d.median);
                            const minY = yScale(d.min);
                            const maxY = yScale(d.max);
                            const fill = CHART_COLORS[i % CHART_COLORS.length];
                            return (
                                <g key={i}>
                                    {/* Whisker line */}
                                    <line x1={cx} y1={maxY} x2={cx} y2={minY} stroke="hsl(var(--foreground))" strokeWidth="0.6" />
                                    <line x1={cx - 5} y1={maxY} x2={cx + 5} y2={maxY} stroke="hsl(var(--foreground))" strokeWidth="0.6" />
                                    <line x1={cx - 5} y1={minY} x2={cx + 5} y2={minY} stroke="hsl(var(--foreground))" strokeWidth="0.6" />
                                    {/* Box */}
                                    <rect x={cx - 8} y={boxTop} width="16" height={Math.max(1, boxBottom - boxTop)} fill={fill} opacity="0.5" stroke={fill} strokeWidth="0.6" />
                                    {/* Median line */}
                                    <line x1={cx - 8} y1={medianY} x2={cx + 8} y2={medianY} stroke={fill} strokeWidth="1.2" />
                                    <text x={cx} y="98" fontSize="5" textAnchor="middle" fill="hsl(var(--muted-foreground))">{d.label}</text>
                                </g>
                            );
                        })}
                    </svg>
                );
            }}
        </WidgetFrame>
    );
}

// ─────────────────────────────────────────────────────────────────────
// NETWORK GRAPH — circular node layout + edges
// ─────────────────────────────────────────────────────────────────────
export function NetworkGraphWidget({ w, stale }) {
    return (
        <WidgetFrame w={w} stale={stale} testIdSuffix="network">
            {(cfg) => {
                // Expects cfg.nodes = [{id, label, group?}, ...]; cfg.links = [{source, target, value?}]
                const nodes = Array.isArray(cfg.nodes) ? cfg.nodes : [];
                const links = Array.isArray(cfg.links) ? cfg.links : [];
                if (!nodes.length) {
                    return <div className="flex items-center justify-center h-full text-xs text-muted-foreground">No nodes</div>;
                }
                // Position nodes evenly on a circle
                const N = nodes.length;
                const cx = 50, cy = 50, r = 38;
                const pos = {};
                nodes.forEach((n, i) => {
                    const a = (i / N) * 2 * Math.PI - Math.PI / 2;
                    pos[n.id] = { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
                });
                const groups = Array.from(new Set(nodes.map((n) => n.group || "default")));
                const colorFor = (g) => CHART_COLORS[groups.indexOf(g) % CHART_COLORS.length];
                return (
                    <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
                        {/* Edges */}
                        {links.map((l, i) => {
                            const s = pos[l.source];
                            const t = pos[l.target];
                            if (!s || !t) return null;
                            const sw = 0.3 + Math.min((Number(l.value) || 1) / 10, 1.5);
                            return <line key={i} x1={s.x} y1={s.y} x2={t.x} y2={t.y}
                                stroke="hsl(var(--muted-foreground))" strokeWidth={sw} opacity="0.4" />;
                        })}
                        {/* Nodes */}
                        {nodes.map((n) => {
                            const p = pos[n.id];
                            return (
                                <g key={n.id}>
                                    <circle cx={p.x} cy={p.y} r="3.5" fill={colorFor(n.group || "default")} stroke="hsl(var(--background))" strokeWidth="0.6" />
                                    <text x={p.x} y={p.y - 5} fontSize="4" textAnchor="middle" fill="hsl(var(--foreground))">{n.label || n.id}</text>
                                </g>
                            );
                        })}
                    </svg>
                );
            }}
        </WidgetFrame>
    );
}

// ─────────────────────────────────────────────────────────────────────
// CANDLESTICK — OHLC bars (open / high / low / close per period)
// ─────────────────────────────────────────────────────────────────────
export function CandlestickWidget({ w, stale }) {
    return (
        <WidgetFrame w={w} stale={stale} testIdSuffix="candlestick">
            {(cfg) => {
                // Expects cfg.data = [{label, open, high, low, close}, ...]
                const data = Array.isArray(cfg.data) ? cfg.data : [];
                if (!data.length) {
                    return <div className="flex items-center justify-center h-full text-xs text-muted-foreground">No data</div>;
                }
                const allValues = data.flatMap((d) => [d.open, d.high, d.low, d.close].filter((v) => v != null));
                const dataMin = Math.min(...allValues);
                const dataMax = Math.max(...allValues);
                const range = (dataMax - dataMin) || 1;
                const yScale = (v) => 90 - ((v - dataMin) / range) * 80;
                const stepX = Math.max(20, 100 / Math.max(data.length, 1));
                return (
                    <svg viewBox={`0 0 ${Math.max(140, data.length * 16)} 100`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
                        {data.map((d, i) => {
                            const cx = i * 16 + 10;
                            const highY = yScale(d.high);
                            const lowY = yScale(d.low);
                            const openY = yScale(d.open);
                            const closeY = yScale(d.close);
                            const up = d.close >= d.open;
                            const color = up ? "hsl(140 65% 45%)" : "hsl(0 70% 50%)";
                            const bodyTop = Math.min(openY, closeY);
                            const bodyHeight = Math.max(1, Math.abs(closeY - openY));
                            return (
                                <g key={i}>
                                    <line x1={cx} y1={highY} x2={cx} y2={lowY} stroke={color} strokeWidth="0.8" />
                                    <rect x={cx - 4} y={bodyTop} width="8" height={bodyHeight} fill={color} />
                                    {i % Math.ceil(data.length / 8) === 0 && (
                                        <text x={cx} y="98" fontSize="4" textAnchor="middle" fill="hsl(var(--muted-foreground))">{d.label}</text>
                                    )}
                                </g>
                            );
                        })}
                    </svg>
                );
            }}
        </WidgetFrame>
    );
}
